/**
 * Centralized Teacher Tools Configuration
 *
 * This file contains all configuration for teacher tools including:
 * - Tool metadata (title, description, routes, icons)
 * - Dynamic form field definitions
 * - API payload transformations
 * - Canvas message templates
 * - Mint export (`mintExport`): tools whose canvas can be added to a power lesson as a Mint page;
 *   `isTeacherOnly` creates the page hidden from students. Tools without it offer no such action.
 */

// Maps special needs codes (sent to the backend) to chatbot i18n keys used to translate them in the UI.
const SPECIAL_NEEDS_TRANSLATION_KEYS = {
  high_abilities: 'High abilities',
  tea: 'TEA',
  tdah: 'TDAH',
  dyslexia: 'Dyslexia',
  dysgrafia: 'Dysgrafia',
  dyscalculia: 'Dyscalculia',
  language_disorders: 'Language disorders',
};

// Maps rubric scale codes (sent to the backend) to chatbot i18n keys used to translate them in the UI.
const RUBRIC_SCALE_TRANSLATION_KEYS = {
  '1-4': 'teacher_tools_rubric_scale_1_4',
  '1-6': 'teacher_tools_rubric_scale_1_6',
  '1-10': 'teacher_tools_rubric_scale_1_10',
};

// Field type constants for dynamic form generation
export const FIELD_TYPES = {
  EDUCATION_YEAR: 'EDUCATION_YEAR',
  TEXT_OR_LESSON: 'TEXT_OR_LESSON',
  TEXTAREA: 'TEXTAREA',
  TEXTAREA_WITH_COUNT: 'TEXTAREA_WITH_COUNT',
  TEXT: 'TEXT',
  NUMBER: 'NUMBER',
  DROPDOWN: 'DROPDOWN',
  SPECIAL_NEEDS: 'SPECIAL_NEEDS',
  ADDITIONAL_NOTES: 'ADDITIONAL_NOTES',
  ACCORDION_GROUP: 'ACCORDION_GROUP',
  EVALUATION_CRITERIA: 'EVALUATION_CRITERIA',
};

// Static tool configuration with i18n keys
export const TEACHER_TOOLS = [
  {
    id: 'question-generator',
    titleKey: 'tool_question_generator_title',
    descriptionKey: 'tool_question_generator_description',
    longDescriptionKey: 'tool_question_generator_long_description',
    icon: 'sparkles',
    iconColor: '#3B82F6', // Blue
    category: 'assessment',
    available: true,
    opensDialog: 'QuizLocationDialog',
    displayConfig: [],
  },
  {
    id: 'text-level-adapter',
    titleKey: 'tool_text_level_adapter_title',
    descriptionKey: 'tool_text_level_adapter_description',
    longDescriptionKey: 'tool_text_level_adapter_long_description',
    setupRoute: '/teacher-tools/setup/text-level-adapter',
    icon: 'pencil-color',
    iconColor: '#8B5CF6', // Purple
    category: 'content',
    available: true,
    mintExport: { isTeacherOnly: false },
    // Dynamic form configuration
    fields: [
      {
        id: 'education',
        type: FIELD_TYPES.EDUCATION_YEAR,
        required: true,
        showLabel: true,
        order: 1,
      },
      {
        id: 'textOrLesson',
        type: FIELD_TYPES.TEXT_OR_LESSON,
        required: true,
        labelKey: 'teacher_tools_adapter_text_label',
        placeholderKey: 'Write, paste text or choose from your books',
        showLabel: true,
        order: 2,
      },
      {
        id: 'specialNeeds',
        type: FIELD_TYPES.SPECIAL_NEEDS,
        required: false,
        labelKey: 'teacher_tools_special_needs_label',
        order: 3,
      },
      {
        id: 'additionalNotes',
        type: FIELD_TYPES.ADDITIONAL_NOTES,
        required: false,
        labelKey: 'teacher_tools_additional_notes_label',
        placeholderKey: 'teacher_tools_additional_notes_placeholder',
        rows: 3,
        order: 4,
      },
    ],
    transformPayload: (formData) => {
      return {
        educationYear: formData.education.educationYearLabel,
        educationLevel: formData.education.educationLevelLabel,
        textToAdapt: formData.textOrLesson.text,
        specialNeeds: formData.specialNeeds?.length > 0 ? formData.specialNeeds.join(', ') : undefined,
        additionalNotes: formData.additionalNotes || undefined,
      };
    },
    apiMethod: 'createTextLevelAdapter',
    canvasMessageKey: 'teacher_tools_text_level_adapter_canvas_message',
    displayConfig: [
      { key: 'educationLevel', keys: ['educationYear', 'educationLevel'], labelKey: 'teacher_tools_selected_education_label', type: 'text' },
      { key: 'textToAdapt', labelKey: 'teacher_tools_selected_contents_label', type: 'text', clickableOnly: true },
      { key: 'specialNeeds', labelKey: 'teacher_tools_special_needs_label', type: 'array', valueMap: SPECIAL_NEEDS_TRANSLATION_KEYS },
      { key: 'additionalNotes', labelKey: 'teacher_tools_selected_additional_notes_label', type: 'text' },
    ],
  },
  {
    id: 'lesson-plan-generator',
    titleKey: 'tool_lesson_plan_generator_title',
    descriptionKey: 'tool_lesson_plan_generator_description',
    longDescriptionKey: 'tool_lesson_plan_generator_long_description',
    setupRoute: '/teacher-tools/setup/lesson-plan-generator',
    icon: 'magic-wand-color',
    iconColor: '#10B981', // Green
    category: 'planning',
    available: true,
    mintExport: { isTeacherOnly: true },
    // Dynamic form configuration
    fields: [
      {
        id: 'education',
        type: FIELD_TYPES.EDUCATION_YEAR,
        required: true,
        showLabel: true,
        order: 1,
      },
      {
        id: 'textOrLesson',
        type: FIELD_TYPES.TEXT_OR_LESSON,
        required: true,
        labelKey: 'teacher_tools_topic_label',
        placeholderKey: 'Write, paste text or choose from your books',
        showLabel: true,
        order: 2,
      },
      {
        id: 'lessonDuration',
        type: FIELD_TYPES.NUMBER,
        required: true,
        labelKey: 'teacher_tools_lesson_duration_label',
        placeholderKey: 'teacher_tools_lesson_duration_placeholder',
        min: 15,
        max: 180,
        defaultValue: 45,
        order: 3,
      },
      {
        id: 'optionalDetails',
        type: FIELD_TYPES.ACCORDION_GROUP,
        labelKey: 'teacher_tools_optional_details_label',
        order: 4,
        fields: [
          {
            id: 'evaluationCriteria',
            type: FIELD_TYPES.EVALUATION_CRITERIA,
            required: false,
            order: 1,
          },
          {
            id: 'specialNeeds',
            type: FIELD_TYPES.SPECIAL_NEEDS,
            required: false,
            labelKey: 'teacher_tools_special_needs_label',
            order: 2,
          },
          {
            id: 'availableResources',
            type: FIELD_TYPES.TEXTAREA,
            required: false,
            labelKey: 'teacher_tools_available_resources_label',
            placeholderKey: 'teacher_tools_available_resources_placeholder',
            rows: 3,
            order: 3,
          },
          {
            id: 'additionalNotes',
            type: FIELD_TYPES.ADDITIONAL_NOTES,
            required: false,
            labelKey: 'teacher_tools_additional_notes_label',
            placeholderKey: 'teacher_tools_additional_notes_placeholder',
            rows: 3,
            order: 4,
          },
        ],
      },
    ],
    transformPayload: (formData) => {
      const payload = {
        educationYear: formData.education?.educationYearLabel,
        educationLevel: formData.education?.educationLevelLabel,
        textToAdapt: formData.textOrLesson?.text,
        duration: `${formData.lessonDuration || 45} minutos`,
      };

      // Build evaluationCriteria array from composite field
      const criteriaArray = [];

      // Add selected learning objectives
      if (formData.evaluationCriteria?.selected?.length > 0) {
        const selectedLabels = formData.evaluationCriteria.selected
          .map((lo) => (typeof lo === 'string' ? lo : lo?.description ?? lo?.learning_objective))
          .filter((label) => typeof label === 'string' && label.length > 0);
        criteriaArray.push(...selectedLabels);
      }

      // Parse additional criteria (line-separated text)
      if (formData.evaluationCriteria?.additional) {
        const additionalParsed = formData.evaluationCriteria.additional
          .split(/\n/)
          .map((c) => c.trim())
          .filter((c) => c.length > 0);
        criteriaArray.push(...additionalParsed);
      }

      // Only add evaluationCriteria if we have at least one criterion
      if (criteriaArray.length > 0) {
        payload.evaluationCriteria = criteriaArray;
      }

      // Add other optional fields
      if (formData.specialNeeds?.length > 0) {
        payload.specialNeeds = formData.specialNeeds.join(', ');
      }
      if (formData.availableResources) {
        payload.resources = formData.availableResources;
      }
      if (formData.additionalNotes) {
        payload.additionalNotes = formData.additionalNotes;
      }

      return payload;
    },
    apiMethod: 'createLessonPlanGenerator',
    canvasMessageKey: 'teacher_tools_lesson_plan_generator_canvas_message',
    displayConfig: [
      { key: 'educationLevel', keys: ['educationYear', 'educationLevel'], labelKey: 'teacher_tools_selected_education_label', type: 'text' },
      { key: 'textToAdapt', labelKey: 'teacher_tools_selected_contents_label', type: 'text', clickableOnly: true },
      { key: 'duration', labelKey: 'teacher_tools_selected_duration_label', type: 'text' },
      { key: 'evaluationCriteria', labelKey: 'teacher_tools_selected_evaluation_criteria_label', type: 'array' },
      { key: 'specialNeeds', labelKey: 'teacher_tools_special_needs_label', type: 'array', valueMap: SPECIAL_NEEDS_TRANSLATION_KEYS },
      { key: 'resources', labelKey: 'teacher_tools_selected_resources_label', type: 'text' },
      { key: 'additionalNotes', labelKey: 'teacher_tools_selected_additional_notes_label', type: 'text' },
    ],
  },
  {
    id: 'real-world-connection',
    titleKey: 'tool_real_world_connection_title',
    descriptionKey: 'tool_real_world_connection_description',
    longDescriptionKey: 'tool_real_world_connection_long_description',
    setupRoute: '/teacher-tools/setup/real-world-connection',
    icon: 'globe-showing-europe-africa',
    iconColor: '#EC4899', // Pink
    category: 'engagement',
    available: true,
    mintExport: { isTeacherOnly: false },
    // Dynamic form configuration
    fields: [
      {
        id: 'education',
        type: FIELD_TYPES.EDUCATION_YEAR,
        required: true,
        showLabel: true,
        order: 1,
      },
      {
        id: 'textOrLesson',
        type: FIELD_TYPES.TEXT_OR_LESSON,
        required: true,
        labelKey: 'teacher_tools_real_world_text_label',
        placeholderKey: 'Write, paste text or choose from your books',
        showLabel: true,
        order: 2,
      },
      {
        id: 'additionalNotes',
        type: FIELD_TYPES.ADDITIONAL_NOTES,
        required: false,
        labelKey: 'teacher_tools_additional_notes_label',
        placeholderKey: 'teacher_tools_additional_notes_placeholder',
        rows: 3,
        order: 3,
      },
    ],
    transformPayload: (formData) => {
      return {
        educationYear: formData.education.educationYearLabel,
        educationLevel: formData.education.educationLevelLabel,
        textToAdapt: formData.textOrLesson.text,
        additionalNotes: formData.additionalNotes || undefined,
      };
    },
    apiMethod: 'createRealWorldConnection',
    canvasMessageKey: 'teacher_tools_real_world_connection_canvas_message',
    displayConfig: [
      { key: 'educationLevel', keys: ['educationYear', 'educationLevel'], labelKey: 'teacher_tools_selected_education_label', type: 'text' },
      { key: 'textToAdapt', labelKey: 'teacher_tools_selected_contents_label', type: 'text', clickableOnly: true },
      { key: 'additionalNotes', labelKey: 'teacher_tools_selected_additional_notes_label', type: 'text' },
    ],
  },
  {
    id: 'rubric-generator',
    titleKey: 'tool_rubric_generator_title',
    descriptionKey: 'tool_rubric_generator_description',
    longDescriptionKey: 'tool_rubric_generator_long_description',
    setupRoute: '/teacher-tools/setup/rubric-generator',
    icon: 'homework',
    iconColor: '#F59E0B', // Orange
    category: 'assessment',
    available: true,
    mintExport: { isTeacherOnly: true },
    // Dynamic form configuration
    fields: [
      {
        id: 'education',
        type: FIELD_TYPES.EDUCATION_YEAR,
        required: true,
        showLabel: true,
        order: 1,
      },
      {
        id: 'textOrLesson',
        type: FIELD_TYPES.TEXT_OR_LESSON,
        required: true,
        labelKey: 'teacher_tools_rubric_text_label',
        placeholderKey: 'teacher_tools_rubric_text_placeholder',
        showLabel: true,
        order: 2,
      },
      {
        id: 'numberOfCriteria',
        type: FIELD_TYPES.NUMBER,
        required: true,
        labelKey: 'teacher_tools_rubric_criteria_count_label',
        placeholderKey: 'teacher_tools_rubric_criteria_count_placeholder',
        min: 1,
        max: 8,
        defaultValue: 4,
        order: 3,
      },
      {
        id: 'scale',
        type: FIELD_TYPES.DROPDOWN,
        required: true,
        labelKey: 'teacher_tools_rubric_scale_label',
        order: 4,
        isMulti: false,
        options: [
          { value: '1-4', labelKey: 'teacher_tools_rubric_scale_1_4' },
          { value: '1-6', labelKey: 'teacher_tools_rubric_scale_1_6' },
          { value: '1-10', labelKey: 'teacher_tools_rubric_scale_1_10' },
        ],
      },
      {
        id: 'optionalDetails',
        type: FIELD_TYPES.ACCORDION_GROUP,
        labelKey: 'teacher_tools_optional_details_label',
        order: 5,
        fields: [
          {
            id: 'evaluationCriteria',
            type: FIELD_TYPES.EVALUATION_CRITERIA,
            required: false,
            order: 1,
          },
          {
            id: 'specialNeeds',
            type: FIELD_TYPES.SPECIAL_NEEDS,
            required: false,
            labelKey: 'teacher_tools_special_needs_label',
            order: 2,
          },
          {
            id: 'additionalNotes',
            type: FIELD_TYPES.ADDITIONAL_NOTES,
            required: false,
            labelKey: 'teacher_tools_additional_notes_label',
            placeholderKey: 'teacher_tools_additional_notes_placeholder',
            rows: 3,
            order: 3,
          },
        ],
      },
    ],
    transformPayload: (formData) => {
      const payload = {
        educationYear: formData.education?.educationYearLabel,
        educationLevel: formData.education?.educationLevelLabel,
        textToAdapt: formData.textOrLesson?.text,
        numberOfCriteria: formData.numberOfCriteria || 4,
        scale: formData.scale,
      };

      // Build evaluationCriteria array from composite field
      const criteriaArray = [];

      // Add selected learning objectives
      if (formData.evaluationCriteria?.selected?.length > 0) {
        const selectedLabels = formData.evaluationCriteria.selected
          .map((lo) => (typeof lo === 'string' ? lo : lo?.description ?? lo?.learning_objective))
          .filter((label) => typeof label === 'string' && label.length > 0);
        criteriaArray.push(...selectedLabels);
      }

      // Parse additional criteria (line-separated text)
      if (formData.evaluationCriteria?.additional) {
        const additionalParsed = formData.evaluationCriteria.additional
          .split(/\n/)
          .map((c) => c.trim())
          .filter((c) => c.length > 0);
        criteriaArray.push(...additionalParsed);
      }

      // Only add evaluationCriteria if we have at least one criterion
      if (criteriaArray.length > 0) {
        payload.evaluationCriteria = criteriaArray;
      }

      // Add other optional fields
      if (formData.specialNeeds?.length > 0) {
        payload.specialNeeds = formData.specialNeeds.join(', ');
      }
      if (formData.additionalNotes) {
        payload.additionalNotes = formData.additionalNotes;
      }

      return payload;
    },
    apiMethod: 'createRubricGenerator',
    canvasMessageKey: 'teacher_tools_rubric_generator_canvas_message',
    displayConfig: [
      { key: 'educationLevel', keys: ['educationYear', 'educationLevel'], labelKey: 'teacher_tools_selected_education_label', type: 'text' },
      { key: 'textToAdapt', labelKey: 'teacher_tools_selected_contents_label', type: 'text', clickableOnly: true },
      { key: 'numberOfCriteria', labelKey: 'teacher_tools_selected_number_of_criteria_label', type: 'text' },
      { key: 'scale', labelKey: 'teacher_tools_selected_scale_label', type: 'text', valueMap: RUBRIC_SCALE_TRANSLATION_KEYS },
      { key: 'evaluationCriteria', labelKey: 'teacher_tools_selected_evaluation_criteria_label', type: 'array' },
      { key: 'specialNeeds', labelKey: 'teacher_tools_special_needs_label', type: 'array', valueMap: SPECIAL_NEEDS_TRANSLATION_KEYS },
      { key: 'additionalNotes', labelKey: 'teacher_tools_selected_additional_notes_label', type: 'text' },
    ],
  },
  {
    id: 'vocabulary-creator',
    titleKey: 'tool_vocabulary_creator_title',
    descriptionKey: 'tool_vocabulary_creator_description',
    longDescriptionKey: 'tool_vocabulary_creator_long_description',
    setupRoute: '/teacher-tools/setup/vocabulary-creator',
    icon: 'books',
    iconColor: '#14B8A6', // Teal
    category: 'content',
    available: true,
    mintExport: { isTeacherOnly: false },
    // Dynamic form configuration
    fields: [
      {
        id: 'education',
        type: FIELD_TYPES.EDUCATION_YEAR,
        required: true,
        showLabel: true,
        order: 1,
      },
      {
        id: 'textOrLesson',
        type: FIELD_TYPES.TEXT_OR_LESSON,
        required: true,
        labelKey: 'teacher_tools_vocabulary_text_label',
        placeholderKey: 'Write, paste text or choose from your books',
        showLabel: true,
        order: 2,
      },
      {
        id: 'numberOfWordsToExtract',
        type: FIELD_TYPES.NUMBER,
        required: true,
        labelKey: 'teacher_tools_vocabulary_word_count_label',
        placeholderKey: 'teacher_tools_vocabulary_word_count_placeholder',
        min: 1,
        max: 50,
        defaultValue: 10,
        order: 3,
      },
      {
        id: 'specialNeeds',
        type: FIELD_TYPES.SPECIAL_NEEDS,
        required: false,
        labelKey: 'teacher_tools_special_needs_label',
        order: 4,
      },
      {
        id: 'additionalNotes',
        type: FIELD_TYPES.ADDITIONAL_NOTES,
        required: false,
        labelKey: 'teacher_tools_additional_notes_label',
        placeholderKey: 'teacher_tools_additional_notes_placeholder',
        rows: 3,
        order: 5,
      },
    ],
    transformPayload: (formData) => {
      return {
        educationYear: formData.education.educationYearLabel,
        educationLevel: formData.education.educationLevelLabel,
        textToAdapt: formData.textOrLesson.text,
        numberOfWordsToExtract: formData.numberOfWordsToExtract || 10,
        specialNeeds: formData.specialNeeds?.length > 0 ? formData.specialNeeds.join(', ') : undefined,
        additionalNotes: formData.additionalNotes || undefined,
      };
    },
    apiMethod: 'createVocabularyCreator',
    canvasMessageKey: 'teacher_tools_vocabulary_creator_canvas_message',
    displayConfig: [
      { key: 'educationLevel', keys: ['educationYear', 'educationLevel'], labelKey: 'teacher_tools_selected_education_label', type: 'text' },
      { key: 'textToAdapt', labelKey: 'teacher_tools_selected_contents_label', type: 'text', clickableOnly: true },
      { key: 'numberOfWordsToExtract', labelKey: 'teacher_tools_selected_number_of_words_label', type: 'text' },
      { key: 'specialNeeds', labelKey: 'teacher_tools_special_needs_label', type: 'array', valueMap: SPECIAL_NEEDS_TRANSLATION_KEYS },
      { key: 'additionalNotes', labelKey: 'teacher_tools_selected_additional_notes_label', type: 'text' },
    ],
  },
  {
    id: 'text-translator',
    titleKey: 'tool_text_translator_title',
    descriptionKey: 'tool_text_translator_description',
    longDescriptionKey: 'tool_text_translator_long_description',
    setupRoute: '/teacher-tools/setup/text-translator',
    icon: 'globe-with-meridians',
    iconColor: '#6366F1', // Indigo
    category: 'content',
    available: true,
    mintExport: { isTeacherOnly: false },
    // Dynamic form configuration
    fields: [
      {
        id: 'education',
        type: FIELD_TYPES.EDUCATION_YEAR,
        required: true,
        showLabel: true,
        order: 1,
      },
      {
        id: 'textOrLesson',
        type: FIELD_TYPES.TEXT_OR_LESSON,
        required: true,
        labelKey: 'teacher_tools_text_to_translate_label',
        placeholderKey: 'Write, paste text or choose from your books',
        showLabel: true,
        order: 2,
      },
      {
        id: 'destinationLanguage',
        type: FIELD_TYPES.DROPDOWN,
        required: true,
        labelKey: 'teacher_tools_destination_language_label',
        order: 3,
        isMulti: false,
        sortOptions: true,
        options: [
          { value: 'es', labelKey: 'language_es' },
          { value: 'ca-valencia', labelKey: 'language_ca_valencia' },
          { value: 'ca', labelKey: 'language_ca' },
          { value: 'eu', labelKey: 'language_eu' },
          { value: 'gl', labelKey: 'language_gl' },
          { value: 'en', labelKey: 'language_en' },
          { value: 'fr', labelKey: 'language_fr' },
          { value: 'it', labelKey: 'language_it' },
          { value: 'zh', labelKey: 'language_zh' },
          { value: 'ar', labelKey: 'language_ar' },
          { value: 'pt-BR', labelKey: 'language_pt_br' },
        ],
      },
      {
        id: 'specialNeeds',
        type: FIELD_TYPES.SPECIAL_NEEDS,
        required: false,
        labelKey: 'teacher_tools_special_needs_label',
        order: 4,
      },
      {
        id: 'additionalNotes',
        type: FIELD_TYPES.ADDITIONAL_NOTES,
        required: false,
        labelKey: 'teacher_tools_additional_notes_label',
        placeholderKey: 'teacher_tools_additional_notes_placeholder',
        rows: 3,
        order: 5,
      },
    ],
    transformPayload: (formData) => {
      return {
        educationYear: formData.education.educationYearLabel,
        educationLevel: formData.education.educationLevelLabel,
        textToAdapt: formData.textOrLesson.text,
        destinationLanguage: formData.destinationLanguage,
        specialNeeds: formData.specialNeeds?.length > 0 ? formData.specialNeeds.join(', ') : undefined,
        additionalNotes: formData.additionalNotes || undefined,
      };
    },
    apiMethod: 'createTextTranslator',
    canvasMessageKey: 'teacher_tools_text_translator_canvas_message',
    displayConfig: [
      { key: 'educationLevel', keys: ['educationYear', 'educationLevel'], labelKey: 'teacher_tools_selected_education_label', type: 'text' },
      { key: 'textToAdapt', labelKey: 'teacher_tools_selected_contents_label', type: 'text', clickableOnly: true },
      { key: 'destinationLanguage', labelKey: 'teacher_tools_selected_destination_language_label', type: 'text' },
      { key: 'specialNeeds', labelKey: 'teacher_tools_special_needs_label', type: 'array', valueMap: SPECIAL_NEEDS_TRANSLATION_KEYS },
      { key: 'additionalNotes', labelKey: 'teacher_tools_selected_additional_notes_label', type: 'text' },
    ],
  },
  {
    id: 'classroom-energizer',
    titleKey: 'tool_classroom_energizer_title',
    descriptionKey: 'tool_classroom_energizer_description',
    longDescriptionKey: 'tool_classroom_energizer_long_description',
    setupRoute: '/teacher-tools/setup/classroom-energizer',
    icon: 'party-popper',
    iconColor: '#EF4444', // Red
    category: 'engagement',
    available: true,
    mintExport: { isTeacherOnly: false },
    fields: [
      {
        id: 'education',
        type: FIELD_TYPES.EDUCATION_YEAR,
        required: true,
        showLabel: true,
        order: 1,
      },
      {
        id: 'textOrLesson',
        type: FIELD_TYPES.TEXT_OR_LESSON,
        required: true,
        labelKey: 'teacher_tools_energizer_text_label',
        placeholderKey: 'Write, paste text or choose from your books',
        showLabel: true,
        order: 2,
      },
      {
        id: 'additionalNotes',
        type: FIELD_TYPES.ADDITIONAL_NOTES,
        required: false,
        labelKey: 'teacher_tools_additional_notes_label',
        placeholderKey: 'teacher_tools_additional_notes_placeholder',
        rows: 3,
        order: 3,
      },
    ],
    transformPayload: (formData) => {
      return {
        educationYear: formData.education.educationYearLabel,
        educationLevel: formData.education.educationLevelLabel,
        textToAdapt: formData.textOrLesson.text,
        additionalNotes: formData.additionalNotes || undefined,
      };
    },
    apiMethod: 'createClassroomDynamics',
    canvasMessageKey: 'teacher_tools_classroom_energizer_canvas_message',
    displayConfig: [
      { key: 'educationLevel', keys: ['educationYear', 'educationLevel'], labelKey: 'teacher_tools_selected_education_label', type: 'text' },
      { key: 'textToAdapt', labelKey: 'teacher_tools_selected_contents_label', type: 'text', clickableOnly: true },
      { key: 'additionalNotes', labelKey: 'teacher_tools_selected_additional_notes_label', type: 'text' },
    ],
  },
  {
    id: 'student-feedback',
    titleKey: 'tool_student_feedback_title',
    descriptionKey: 'tool_student_feedback_description',
    longDescriptionKey: 'tool_student_feedback_long_description',
    setupRoute: '/teacher-tools/setup/student-feedback',
    icon: 'memo',
    iconColor: '#06B6D4', // Cyan
    category: 'communication',
    available: true,
    // No mintExport: the feedback is about one student, and a lesson page would show it to the whole class.
    // Dynamic form configuration
    fields: [
      {
        id: 'education',
        type: FIELD_TYPES.EDUCATION_YEAR,
        required: true,
        showLabel: true,
        order: 1,
      },
      {
        id: 'studentWork',
        type: FIELD_TYPES.TEXTAREA_WITH_COUNT,
        required: true,
        labelKey: 'teacher_tools_student_work_label',
        placeholderKey: 'teacher_tools_student_work_placeholder',
        rows: 3,
        order: 2,
      },
      {
        id: 'expectedResponse',
        type: FIELD_TYPES.TEXTAREA_WITH_COUNT,
        required: false,
        labelKey: 'teacher_tools_expected_response_label',
        placeholderKey: 'teacher_tools_expected_response_placeholder',
        rows: 3,
        order: 3,
      },
      {
        id: 'optionalDetails',
        type: FIELD_TYPES.ACCORDION_GROUP,
        labelKey: 'teacher_tools_optional_details_label',
        order: 4,
        fields: [
          {
            id: 'evaluationCriteria',
            type: FIELD_TYPES.EVALUATION_CRITERIA,
            required: false,
            order: 1,
          },
          {
            id: 'specialNeeds',
            type: FIELD_TYPES.SPECIAL_NEEDS,
            required: false,
            labelKey: 'teacher_tools_special_needs_label',
            order: 2,
          },
          {
            id: 'additionalNotes',
            type: FIELD_TYPES.ADDITIONAL_NOTES,
            required: false,
            labelKey: 'teacher_tools_additional_notes_label',
            placeholderKey: 'teacher_tools_additional_notes_placeholder',
            rows: 3,
            order: 3,
          },
        ],
      },
    ],
    transformPayload: (formData) => {
      const payload = {
        educationYear: formData.education?.educationYearLabel,
        educationLevel: formData.education?.educationLevelLabel,
        textToAdapt: formData.studentWork,
      };

      // Expected response (optional)
      if (formData.expectedResponse) {
        payload.expectedResponse = formData.expectedResponse;
      }

      // Build evaluationCriteria array from composite field
      const criteriaArray = [];

      // Add selected learning objectives
      if (formData.evaluationCriteria?.selected?.length > 0) {
        const selectedLabels = formData.evaluationCriteria.selected
          .map((lo) => (typeof lo === 'string' ? lo : lo?.description ?? lo?.learning_objective))
          .filter((label) => typeof label === 'string' && label.length > 0);
        criteriaArray.push(...selectedLabels);
      }

      // Parse additional criteria (line-separated text)
      if (formData.evaluationCriteria?.additional) {
        const additionalParsed = formData.evaluationCriteria.additional
          .split(/\n/)
          .map((c) => c.trim())
          .filter((c) => c.length > 0);
        criteriaArray.push(...additionalParsed);
      }

      // Only add evaluationCriteria if we have at least one criterion
      if (criteriaArray.length > 0) {
        payload.evaluationCriteria = criteriaArray;
      }

      // Add other optional fields
      if (formData.specialNeeds?.length > 0) {
        payload.specialNeeds = formData.specialNeeds.join(', ');
      }
      if (formData.additionalNotes) {
        payload.additionalNotes = formData.additionalNotes;
      }

      return payload;
    },
    apiMethod: 'createStudentFeedback',
    canvasMessageKey: 'teacher_tools_student_feedback_canvas_message',
    displayConfig: [
      { key: 'educationLevel', keys: ['educationYear', 'educationLevel'], labelKey: 'teacher_tools_selected_education_label', type: 'text' },
      { key: 'textToAdapt', labelKey: 'teacher_tools_selected_contents_label', type: 'text', clickableOnly: true },
      { key: 'expectedResponse', labelKey: 'teacher_tools_expected_response_label', type: 'text' },
      { key: 'evaluationCriteria', labelKey: 'teacher_tools_selected_evaluation_criteria_label', type: 'array' },
      { key: 'specialNeeds', labelKey: 'teacher_tools_special_needs_label', type: 'array', valueMap: SPECIAL_NEEDS_TRANSLATION_KEYS },
      { key: 'additionalNotes', labelKey: 'teacher_tools_selected_additional_notes_label', type: 'text' },
    ],
  },
];

// Generate route constants from tool configuration
export const TOOL_ROUTES = TEACHER_TOOLS.reduce((acc, tool) => {
  const key = `TEACHER_TOOL_${tool.id.toUpperCase().replace(/-/g, '_')}`;
  acc[key] = tool.setupRoute;
  return acc;
}, {});

// Backward compatibility - function that returns tools with translated labels
export const getTeacherTools = (t) =>
  TEACHER_TOOLS.map((tool) => ({
    ...tool,
    title: t(tool.titleKey),
    description: t(tool.descriptionKey),
  }));

// Explicit mapping for API mode values to tool IDs
export const TOOL_MODE_MAP = {
  TEACHER_TOOLS_REAL_WORLD_CONNECTION: 'real-world-connection',
  TEACHER_TOOLS_TEXT_LEVEL_ADAPTATION: 'text-level-adapter',
  TEACHER_TOOLS_LESSON_PLAN_GENERATOR: 'lesson-plan-generator',
  TEACHER_TOOLS_RUBRIC_GENERATOR: 'rubric-generator',
  TEACHER_TOOLS_VOCABULARY_CREATOR: 'vocabulary-creator',
  TEACHER_TOOLS_TEXT_TRANSLATOR: 'text-translator',
  TEACHER_TOOLS_STUDENT_FEEDBACK: 'student-feedback',
  TEACHER_TOOLS_QUESTION_GENERATOR: 'question-generator',
  TEACHER_TOOLS_CLASSROOM_DYNAMICS: 'classroom-energizer',
};

// Helper function to parse API mode string to tool ID
export const parseToolMode = (mode) => {
  if (!mode) return null;
  // Use explicit mapping for certainty
  return TOOL_MODE_MAP[mode] || null;
};

// Helper function to get tool configuration by ID (no translation needed)
export const getToolById = (toolId) => {
  if (!toolId) return null;
  return TEACHER_TOOLS.find((tool) => tool.id === toolId);
};

/**
 * Get tool field configuration by tool ID (reads from TEACHER_TOOLS array)
 * @param {string} toolId - Tool identifier (e.g., 'vocabulary-creator')
 * @returns {Object|null} Tool field configuration or null if not found
 */
export const getToolFieldsConfig = (toolId) => {
  const tool = getToolById(toolId);
  if (!tool || !tool.fields) return null;

  return {
    fields: tool.fields,
    transformPayload: tool.transformPayload,
    apiMethod: tool.apiMethod,
    canvasMessageKey: tool.canvasMessageKey,
  };
};

/**
 * Get sorted fields for a tool
 * @param {string} toolId - Tool identifier
 * @returns {Array} Sorted array of field definitions
 */
export const getToolFields = (toolId) => {
  const tool = getToolById(toolId);
  if (!tool || !tool.fields) return [];

  return [...tool.fields].sort((a, b) => a.order - b.order);
};
